import {
  GrabAreaManagementService,
  DefaultHttpService as HttpService,
  PlannerService,
} from '../lib/App';
import { SAMLService } from '../lib/SAMLService';
import { CacheService } from './CacheService';

/**
 * Service container. This class manages different services (dependency injection container).
 *
 * @class ServiceContainer
 */
export class ServiceContainer {
  private registrations = new Map<string, Registration>();

  /**
   * Registers an instance of a service for a particular type.
   *
   * @template T The type of the service to register
   * @param { string } prototype
   * @param { Service } instance The instance of the service to register.
   */
  public register(prototype: string, instance: Service) {
    this.registrations.set(prototype, { instance, prototype });
  }

  /**
   * Resolves a service
   *
   * @param { string } key The type of the service to resolve.
   * @returns { Service } Returns the resolved service.
   */
  public resolve<T extends Service>(key: string): T | undefined {
    const registration = this.registrations.get(key);
    if (!registration || !registration.instance) {
      return undefined;
    }

    return registration.instance;
  }

  /**
   * Gets the http network service.
   *
   * @readonly
   * @type {HttpService}
   */
  public get http(): HttpService | undefined {
    return this.resolve<HttpService>('HttpService');
  }

  /**
   * Gets the planner service.
   *
   * @readonly
   * @type {PlannerService}
   */
  public get planner(): PlannerService | undefined {
    return this.resolve<PlannerService>('PlannerService');
  }

  /**
   * Gets the GAM service.
   *
   * @readonly
   * @type {GrabAreaManagementService}
   */
  public get grabAreaManagement(): GrabAreaManagementService | undefined {
    return this.resolve<GrabAreaManagementService>('GrabAreaManagementService');
  }

  /**
   * Gets the SAML service.
   *
   * @readonly
   * @type {SAMLService}
   */
  public get saml(): SAMLService | undefined {
    return this.resolve<SAMLService>('SAMLService');
  }

  /**
   * Gets the cache service.
   *
   * @readonly
   * @type {CacheService}
   */
  public get cache(): CacheService | undefined {
    return this.resolve<CacheService>('CacheService');
  }
}

/**
 * Represents a dependency registration.
 *
 * @interface Registration
 */
export interface Registration {
  prototype: any;
  instance: any;
}

/**
 * Represents a service contract. Every service should implement this contract.
 *
 * @interface Service
 */
export interface Service {
  constructor: any;
}

/**
 * Represents a class
 *
 * @interface Class
 */
export interface Class {
  prototype: any;
}
